import { getStoreDefinition } from '@/composables/StoreDefinition'
import { DatabaseView } from '@/enums/DatabaseView'
import { EndPoint } from '@/enums/EndPoint'
import { EventStatus } from '@/enums/EventStatus'
import { ManagePersonTab } from '@/enums/ManagePersonTab'
import { SqlComparison } from '@/enums/SqlComparison'
import { SqlOperator } from '@/enums/SqlOperator'
import { SqlOrder } from '@/enums/SqlOrder'
import { TableStoreOptions } from '@/enums/TableStoreOptions'
import type { IPeopleStore } from '@/interfaces/api/IPeopleStore'
import type { IQueryFilter } from '@/interfaces/api/IQueryFilter'
import type { IQueryOptions } from '@/interfaces/api/IQueryOptions'
import type { ISimpleOption } from '@/interfaces/ISimpleOption'
import type { PeopleRecord } from '@/models/PeopleRecord'
import type { TableRecord } from '@/models/TableRecord'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAPIStore } from './ApiStore'

let _store: IPeopleStore

export const usePeopleStore = () => {
  if (_store) {
    return _store
  }

  const option = TableStoreOptions.People
  const useStore = defineStore(option.id, getStoreDefinition(option), {
    persist: {
      storage: localStorage,
      pick: ['itemsPerPage']
    }
  })
  _store = useStore() as unknown as IPeopleStore
  _extend(_store)
  return _store
}

const _extend = (store: IPeopleStore) => {
  const { t } = useI18n({ useScope: 'global' })
  const apiStore = useAPIStore()

  // Before deleting a person, update all related Fit Tests to null.
  // Also delete their past events, and reset their future events to open slots.
  store.beforeDelete = async (item: TableRecord): Promise<void> => {
    // Disconnect Fit Test records
    const fitTestQuery: IQueryOptions = {
      where: [
        {
          field: 'personId',
          comparison: SqlComparison.Equal,
          value1: item.id
        }
      ],
      update: {
        personId: null
      }
    }
    await apiStore.updateMany(EndPoint.FitTests, fitTestQuery, true)

    // Delete past Event records.
    const eventsQuery1: IQueryFilter[] = [
      {
        field: 'attendeeId',
        comparison: SqlComparison.Equal,
        value1: item.id,
        operator: SqlOperator.And
      },
      {
        field: 'dtStart',
        comparison: SqlComparison.LessThan,
        value1: new Date().toISOString()
      }
    ]
    await apiStore.deleteMany(EndPoint.Events, eventsQuery1, true)

    // Reset future Event records to open slots.
    const eventsQuery2: IQueryOptions = {
      where: [
        {
          field: 'attendeeId',
          comparison: SqlComparison.Equal,
          value1: item.id
        }
      ],
      update: {
        attendeeId: null,
        status: EventStatus.Tentative
      }
    }
    await apiStore.updateMany(EndPoint.Events, eventsQuery2, true)
  }

  // Override the default table with a view.
  store.useView(DatabaseView.PeopleWithStatus)

  store.selectedTab = ref<ManagePersonTab>(ManagePersonTab.Info) as unknown as ManagePersonTab

  store.getPersonByEmail = async (email: string): Promise<PeopleRecord | null> => {
    let person: PeopleRecord | null = null
    const query: IQueryOptions = {
      isDistinct: true,
      where: [
        {
          field: 'inactive',
          comparison: SqlComparison.Equal,
          value1: false,
          operator: SqlOperator.And
        },
        {
          field: 'email',
          comparison: SqlComparison.Equal,
          value1: email
        }
      ]
    }
    const response = await apiStore.getMany(EndPoint.People, query)
    if (!response.error && response.data.results.length > 0) {
      person = response.data.results[0] as PeopleRecord
    }
    return person
  }

  store.getDirectReports = async (personId: number): Promise<Array<string>> => {
    const query: IQueryOptions = {
      isDistinct: true,
      fields: ['id', 'firstName', 'lastName', 'idNumber'],
      where: [
        {
          field: 'supervisorId',
          comparison: SqlComparison.Equal,
          value1: personId
        }
      ],
      orderBy: [
        { field: 'lastName', order: SqlOrder.ASC },
        { field: 'firstName', order: SqlOrder.ASC }
      ]
    }
    const response = await apiStore.getMany(EndPoint.People, query)
    let options: Array<string> = []
    if (!response.error) {
      const supervisors = response.data?.results || []
      options = supervisors.map((item) => {
        return `${item.lastName}, ${item.firstName} (${item.idNumber})`
      })
    }
    return options
  }

  store.getSupervisorOptions = async (): Promise<Array<ISimpleOption>> => {
    const query: IQueryOptions = {
      isDistinct: true,
      fields: ['id', 'firstName', 'lastName', 'idNumber'],
      where: [
        {
          field: 'isSupervisor',
          comparison: SqlComparison.Equal,
          value1: true
        }
      ],
      orderBy: [
        { field: 'lastName', order: SqlOrder.ASC },
        { field: 'firstName', order: SqlOrder.ASC }
      ]
    }
    const response = await apiStore.getMany(EndPoint.People, query)
    let options: Array<ISimpleOption> = []
    if (!response.error) {
      const supervisors = response.data?.results || []
      options = supervisors.map((item) => {
        return { title: `${item.lastName}, ${item.firstName} (${item.idNumber})`, value: item.id! }
      })
    }
    options.unshift({ title: t('options.notAssigned'), value: null })
    return options
  }
}
